import { ErrorMessage, Form, Formik } from "formik";
import { StatusMessageSectionType } from "../../../types/state/NotificationState";
import { Popup, PopupBody, PopupFooter } from "../../controls";
import { StatusMessageSection } from "../../status-message/StatusMessageSection";
import MultiSelectField from "../../forms/MultiSelectField";
import { formatUtils } from "../../../utils";
import { useRequestEvalTrial } from "./useRequestEvalTrial";
import { RequestState } from "../../../constants/request-state";
import { PricingProvider } from "../../../types/eval/PricingProvider";
import { TextField } from "../../common/form-fields/TextField";
import { NumberField } from "../../common/form-fields/NumberField";
import { RadioGroupField } from "../../common/form-fields/RadioGroupField";
import { StartEvalSchema } from "./StartEvalSchema";
import { Preloader } from "../../common";

export enum ServiceUsage {
    Yes = "Yes",
    No = "No",
}

const PricingServiceUse = Object.values(ServiceUsage).map(s => ({ value:s }));

const hasPricingServices = (serviceUse: ServiceUsage | string) =>
    serviceUse === ServiceUsage.Yes;

const ProvidersList = [
    { text: "BVAL/Kanerai", value: PricingProvider.BVALKanerai },
    { text: "Houlihan Lokey", value: PricingProvider.HoulihanLokey },
    { text: "IHS Markit", value: PricingProvider.IHSMarkit },
    { text: "JP Pricing Direct", value: PricingProvider.JPPricingDirect },
    { text: "L3 Valuations", value: PricingProvider.L3Valuations },
    { text: "Mountainview", value: PricingProvider.Mountainview },
    {
        text: "PriceServe by Bofa/ICE(IDC)",
        value: PricingProvider.PriceServeByBofa,
    },
    { text: "Refinitive/LSEG", value: PricingProvider.RefinitiveLSEG },
    { text: "Other (provide name below)", value: PricingProvider.Other },
];

const PortfolioSizeList = [
    "0 - 25",
    "26 - 50",
    "51 - 100",
    "101 - 250",
    "> 250",
].map((p) => ({ text: p, value: p }));

const PercentageBreakdownList = [
    { value: "usBSL", text: "US/BSL" },
    { value: "usMM", text: "US/MM" },
    { value: "eurBSL", text: "EUR/BSL" },
    { value: "eurMM", text: "EUR/MM" },
];

export interface EvalValues {
    usePricingService: string;
    pricingProvider: PricingProvider[];
    otherProviderName?: string;
    portfolioSize: string;
    percentageBreakdown: {
        usBSL: number;
        usMM: number;
        eurBSL: number;
        eurMM: number;
    };
}

interface Props {
    onClose: (isSubmitClose?: boolean) => void;
}

function StartEvalPopup({ onClose }: Props) {
    const { requestEvalTrial, requestState } = useRequestEvalTrial();
    const isRequesting = requestState === RequestState.request;

    const handleSubmit = async ({
        usePricingService,
        pricingProvider,
        otherProviderName,
        portfolioSize,
        percentageBreakdown,
    }: EvalValues) => {
        const evalData = {
            usePricingService: hasPricingServices(usePricingService),
            pricingProvider,
            otherProviderName: otherProviderName || undefined,
            portfolioSize,
            usBSL: percentageBreakdown.usBSL || undefined,
            usMM: percentageBreakdown.usMM || undefined,
            eurBSL: percentageBreakdown.eurBSL || undefined,
            eurMM: percentageBreakdown.eurMM || undefined,
        };

        await requestEvalTrial(evalData);
        onClose(true);
    };

    return (
        <Popup
            title="Before you start your EVAL™ trial"
            onClose={() => onClose()}
            modalClass="eval-before-you-start-modal"
        >
            <Formik<EvalValues>
                initialValues={{
                    usePricingService: "",
                    pricingProvider: [],
                    otherProviderName: "",
                    portfolioSize: "",
                    percentageBreakdown: {
                        usBSL: 0,
                        usMM: 0,
                        eurBSL: 0,
                        eurMM: 0,
                    },
                }}
                onSubmit={handleSubmit}
                validationSchema={StartEvalSchema}
            >
                {({ values, setFieldValue, errors, touched }) => {
                    const {
                        usePricingService,
                        pricingProvider,
                        otherProviderName,
                        portfolioSize,
                        percentageBreakdown,
                    } = values;

                    const customProviderListItem =
                        ProvidersList[ProvidersList.length - 1];
                    const withOtherProvider = pricingProvider.includes(
                        customProviderListItem.value
                    );

                    const withServices = hasPricingServices(usePricingService);

                    const withRequiredProviderFields =
                        !withServices ||
                        (withOtherProvider
                            ? !!otherProviderName
                            : !!pricingProvider.length);

                    const isSubmitAvailable =
                        withRequiredProviderFields &&
                        portfolioSize &&
                        Object.values(percentageBreakdown).some((b) => !!b);

                    return (
                        <Form>
                            <PopupBody>
                                <StatusMessageSection
                                    type={StatusMessageSectionType.Alert}
                                >
                                    The trial provides access to the full US BSL
                                    universe for three business days. Please
                                    start only when you’re ready to allocate the
                                    necessary resources. A one-time full data
                                    export from the prior month-end is included.
                                </StatusMessageSection>
                                <p className="margin-b-16 text-sm">Tell us a bit about your current setup.</p>

                                <div>
                                    <h5>
                                        Do you currently use any pricing service? <span className="text-red">*</span>
                                    </h5>
                                    <div className="form-row">
                                        <RadioGroupField
                                            className="radio-group-row"
                                            name="usePricingService"
                                            disabled={isRequesting}
                                            items={PricingServiceUse}
                                            onChangeCallback={(value) => {
                                                if (value === ServiceUsage.No) {
                                                    setFieldValue(
                                                        "pricingProvider",
                                                        []
                                                    );
                                                    setFieldValue(
                                                        "otherProviderName",
                                                        ""
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <MultiSelectField
                                        onSelect={(item, selected) => {
                                            const selectedProviders = selected
                                                ? [
                                                      ...values.pricingProvider,
                                                      item.value,
                                                  ]
                                                : values.pricingProvider.filter(
                                                      (p) => p !== item.value
                                                  );

                                            setFieldValue(
                                                "pricingProvider",
                                                selectedProviders
                                            );
                                        }}
                                        onResetAll={() =>
                                            setFieldValue("pricingProvider", [])
                                        }
                                        dataItems={ProvidersList}
                                        selectedValues={values.pricingProvider}
                                        label="Who is your current provider?"
                                        name="pricingProvider"
                                        placeholder="Select provider(s)"
                                        generateSelectedText={formatUtils.formatLimitedMultiSelectText}
                                        resetButtonText="Reset to default"
                                        errorMessage=""
                                        disabled={!withServices || isRequesting}
                                        required={withServices}
                                        horizontalOrientation
                                    />

                                    {withOtherProvider && (
                                        <TextField
                                            label="Provider Name"
                                            name="otherProviderName"
                                            placeholder="Provider Name"
                                            disabled={isRequesting}
                                            required
                                        />
                                    )}
                                </div>

                                <div>
                                    <h5>
                                        What is the size of your portfolio? <span className="text-red">*</span>
                                    </h5>
                                    <div className="form-row">
                                        <RadioGroupField
                                            name="portfolioSize"
                                            disabled={isRequesting}
                                            items={PortfolioSizeList}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h5>
                                        Please provide the percentage breakdown for: <span className="text-red">*</span>
                                    </h5>
                                    <div className="percentage-breakdown-row">
                                        {PercentageBreakdownList.map(
                                            (breakdown) => (
                                                <NumberField
                                                    key={breakdown.value}
                                                    name={`percentageBreakdown.${breakdown.value}`}
                                                    label={breakdown.text}
                                                    placeholder="0"
                                                    disabled={isRequesting}
                                                />
                                            )
                                        )}
                                    </div>
                                    {touched.percentageBreakdown &&
                                        errors.percentageBreakdown && (
                                            <div className="form-error">
                                                <ErrorMessage name="percentageBreakdown" />
                                            </div>
                                        )}
                                </div>
                            </PopupBody>
                            <PopupFooter>
                                <p className="text-sm">
                                    To learn more about KopenTech products and <br />
                                    services, contact us at{" "}
                                    <a
                                        href={`mailto:${process.env.REACT_APP_SALES_EMAIL_SUBSCRIPTION}`}
                                        className="text-medium"
                                    >
                                        {
                                            process.env.REACT_APP_SALES_EMAIL_SUBSCRIPTION
                                        }
                                    </a>
                                </p>
                                <button
                                    type="submit"
                                    className="btn btn-main"
                                    disabled={
                                        isRequesting || !isSubmitAvailable
                                    }
                                >
                                    Submit {isRequesting && (<Preloader inProgress small />)}
                                </button>
                            </PopupFooter>
                        </Form>
                    );
                }}
            </Formik>
        </Popup>
    );
}

export default StartEvalPopup;

